import { Injectable, inject } from '@angular/core';
import { IFilterService, IQService, ITimeoutService } from 'angular';
import { CartEvent, CartEventAddOn, CartEventItem, CartEventTicket } from '@b2b/types/cart';
import { TODO } from '@b2b/types/core/utils';
import {
  CartEventOrderForm,
  EventDetail,
  EventInventoryAddOn,
  EventMemberRestriction,
  EventPrice,
  EventTimeSlot,
  FoundTicketType,
} from '@b2b/types/event';
import { ApiService } from '../../api/api.service';
import { CalendarService } from '../../calendar/calendar.service';
import { HelpersService } from '../../helpers/helpers.service';
import { PermissionsService } from '../../permissions/permissions.service';
import { SalesRulesService } from '../../sales-rules/sales-rules.service';
import { UserPreferencesService } from '../../user-preferences/user-preferences.service';
import { FILTER_PORT, Q_PORT, TIMEOUT_PORT } from '../../utils';
import { AjsResponse } from '../../api/ajs-response';

/**
 * CartEventsService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/cart/events/events.service.ts
 * Nested sub-service of CartService. Manages event tickets, add-ons, reservations
 * and sales-rule validations inside the shopping cart.
 *
 * Registered via downgradeInjectable in ng-downgraded-components.ts as 'CartEventsService'.
 * Wave 4 — Cart sub-services.
 */

const EVENT_TICKET_DISPLAY_MAX = 5;
const EVENT_ADD_ON_DISPLAY_MAX = 5;

export type CartEventsParent = {
  getTransactingMembershipCategory: () => TODO;
  getTransactingMembership: () => TODO;
  getTransactingMembershipId: () => TODO;
  setVerifyEntitlements: (ignoreLimits: boolean) => void;
  updateCart: () => Promise<TODO>;
  getEventItemById: (eventItem: CartEventItem) => TODO;
  updateItem: (eventItem: CartEventItem) => void;
  addItem: (eventItem: CartEventItem) => void;
  removeItem: (itemId: TODO, skipGetCart: boolean) => TODO;
  removeItems: (itemIds: TODO) => void;
  addCartItem: (eventItem: CartEventItem) => Promise<TODO>;
  updateCartItem: (eventItem: CartEventItem) => Promise<TODO>;
  getReservationId: () => number;
  setReservation: (reservationId?: number) => void;
  getModel: () => TODO;
  getOrganization: () => TODO;
};

@Injectable({ providedIn: 'root' })
export class CartEventsService {
  private $q = inject<IQService>(Q_PORT);
  private $filter = inject<IFilterService>(FILTER_PORT);
  private $timeout = inject<ITimeoutService>(TIMEOUT_PORT);
  private helpersService = inject(HelpersService);
  private apiService = inject(ApiService);
  private calendarService = inject(CalendarService);
  private userPreferencesService = inject(UserPreferencesService);
  private permissionsService = inject(PermissionsService);
  private salesRulesService = inject(SalesRulesService);

  model: any[] = [];
  parent = {} as CartEventsParent;
  events: CartEvent[] = [];
  reservation: Partial<{ id: any; reservationItems: any[] }> | null = null;

  readonly cartEventTicket = {
    itemId: '',
    eventId: '',
    ticketingTypeId: '',
    eventName: '',
    ticketingTypeName: '',
    quantity: 0,
    unitPrice: '0.00',
    retailUnitPrice: '0.00',
    amount: '0.00',
    itemType: 'Event',
    admissionType: '',
    isRetail: false,
    ignoreEntitlements: false,
    displayOrder: 1,
    resolvedQuantity: 0,
    ticketingTypeDescription: '',
    csrDisabled: 0,
    invalid: false,
    total: 0,
    maxLimit: 10,
    groupQuantity: 0,
  };

  readonly CODES = {
    errors: {
      RESERVATION_DNE: 1057,
    },
  };

  dateFormat = this.userPreferencesService.getDateFormat('standardDate', 'dateFormat');

  init(parent: CartEventsParent): this {
    this.parent = parent;
    return this;
  }

  addToCart(event: CartEvent | (Partial<EventDetail> & { ignoreLimits?: boolean })): Promise<any> {
    const q = this.$q.defer();

    if (('tickets' in event && event.tickets && event.tickets.length) || this.getAddOns(event).length) {
      this.addOrUpdateItems(event as CartEvent, (event as CartEvent).tickets, 'Event');
      this.addOrUpdateItems(event as CartEvent, this.getAddOns(event), 'Inventory');

      if ('ignoreLimits' in event && event.ignoreLimits !== undefined) {
        this.parent.setVerifyEntitlements(event.ignoreLimits);
      } else {
        this.parent.setVerifyEntitlements(false);
      }

      this.parent
        .updateCart()
        .then((r) => q.resolve(r))
        .catch((r) => q.reject(r));
    } else if ('id' in event && event.id) {
      this.eventDetail(event.id)
        .then((response) => {
          const eventData = this.processEvent(response.data.payload);
          let tickets = eventData.priceList.prices;
          const allTickets = eventData.priceList.prices;
          let addOns = eventData.addOns ? eventData.addOns.inventoryAddOns : [];
          const allAddOns = eventData.addOns ? eventData.addOns.inventoryAddOns : [];
          let existingItem: TODO;
          let eventItem: TODO;
          let item: TODO;

          if (this.permissionsService.check('CSR')) {
            tickets = tickets.filter((ticket: any) => !ticket.csrDisabled);
          }
          if (!tickets.length) {
            tickets = allTickets;
          }
          if (this.permissionsService.check('CSR')) {
            addOns = addOns.filter((ticket: any) => !ticket.csrDisabled);
          }
          if (!addOns.length) {
            addOns = allAddOns;
          }

          const maxTickets = Math.min(EVENT_TICKET_DISPLAY_MAX, tickets.length);
          const maxAddOns = Math.min(EVENT_ADD_ON_DISPLAY_MAX, addOns.length);

          for (let i = 0; i < maxTickets; i++) {
            item = tickets[i];
            existingItem = item.itemId && this.parent.getEventItemById(item);
            eventItem = {
              quantity: item.quantity || 0,
              isRetail: item.isRetail || false,
              itemType: 'Event',
              eventId: event.id,
              displayOrder: item.displayOrder || 0,
              ticketingTypeId: item.personType.id || null,
              ticketingTypeName: item.personType.name || null,
              unitPrice: item.discountedPrice || null,
              retailUnitPrice: item.price || null,
              ignoreEntitlements: item.isRetail ? false : (event as any).ignoreLimits,
            };
            if (existingItem) {
              eventItem.itemId = existingItem.itemId;
              this.parent.updateItem(eventItem);
            } else {
              this.parent.addItem(eventItem);
              this.parent.addItem(Object.assign({}, eventItem, { isRetail: true }));
            }
          }

          for (let i = 0; i < maxAddOns; i++) {
            item = addOns[i];
            existingItem = item.itemId && this.parent.getEventItemById(item);
            eventItem = {
              quantity: item.quantity || 0,
              itemType: 'Inventory',
              eventId: event.id,
              displayOrder: item.displayOrder || 0,
              inventoryId: item.id || null,
              inventoryName: item.name || null,
              unitPrice: item.price || null,
              retailUnitPrice: item.price || null,
            };
            if (existingItem) {
              eventItem.itemId = existingItem.itemId;
              this.parent.updateItem(eventItem);
            } else {
              this.parent.addItem(eventItem);
            }
          }

          this.parent
            .updateCart()
            .then((r) => q.resolve(r))
            .catch((r) => q.reject(r));
        })
        .catch((r) => q.reject(r));
    } else {
      q.reject();
    }

    return q.promise as Promise<TODO>;
  }

  isEventDiscounted(event: CartEvent, fromMembership = false): boolean {
    let hasDiscount = false;
    const prices = event.event && event.event.priceList.prices ? event.event.priceList.prices : [];
    for (let i = 0, ii = prices.length; i < ii; i++) {
      if (this.isTicketDiscounted(prices[i], event, fromMembership)) {
        hasDiscount = true;
        break;
      }
    }
    return hasDiscount;
  }

  isTicketDiscounted(ticket: CartEventTicket | EventPrice, event: CartEvent, fromMembership = false): boolean {
    const found = this.findTicketType({ ticket, isRetail: false, tickets: event.event?.priceList.prices ?? [] });
    if (!found) return false;
    return fromMembership
      ? this.hasMembershipDiscount(found)
      : this.hasMembershipDiscount(found) || this.hasCouponDiscount(found);
  }

  isTicketDiscountedWithCoupon(ticket: CartEventTicket, event: CartEvent): boolean {
    const found = this.findTicketType({ ticket, isRetail: true, tickets: event.tickets });
    return this.hasCouponDiscount(found);
  }

  hasMembershipDiscount(found: FoundTicketType | null): boolean {
    if (!found) return false;
    return (
      found.ticket.price != null &&
      found.ticket.discountedPrice != null &&
      found.ticket.price !== found.ticket.discountedPrice
    );
  }

  hasCouponDiscount(found: FoundTicketType | null): boolean {
    if (!found) return false;
    return (
      found.ticket.retailUnitPrice != null &&
      found.ticket.unitPrice != null &&
      found.ticket.retailUnitPrice !== found.ticket.unitPrice
    );
  }

  isRetailTicketWithQuantity(ticket: CartEventTicket): boolean {
    return !!(ticket.isRetail && ticket.quantity);
  }

  findTicketType(args: {
    ticket: CartEventTicket | EventPrice;
    isRetail: boolean;
    tickets: (CartEventTicket | EventPrice)[];
  }): FoundTicketType | null {
    let result: FoundTicketType | null = null;
    for (let i = 0, ii = args.tickets.length; i < ii; i++) {
      const isTicketRetail = args.tickets[i].isRetail || false;
      if (args.tickets[i].ticketingTypeId === args.ticket.ticketingTypeId && isTicketRetail === args.isRetail) {
        result = { index: i, ticket: args.tickets[i] };
        break;
      }
    }
    return result;
  }

  removeDiscountedTickets(event: CartEvent, fromMembership = false): Promise<any> {
    let updateCart = false;
    const q = this.$q.defer();
    const isDiscounted = this.isEventDiscounted(event, fromMembership);
    const tickets = isDiscounted ? [] : event.tickets.filter(this.isRetailTicketWithQuantity);

    if (tickets.length) {
      updateCart = true;
    }

    tickets.forEach((ticket: CartEventTicket) => {
      const found = this.findTicketType({ ticket, isRetail: false, tickets: event.tickets });
      if (found) {
        found.ticket.quantity = found.ticket.quantity + ticket.quantity;
        this.addOrUpdateItems(event, [found.ticket as TODO as CartEventTicket], 'Event');
      }
      ticket.quantity = 0;
      this.addOrUpdateItems(event, [ticket], 'Event');
    });

    if (updateCart) {
      this.parent
        .updateCart()
        .then((r) => q.resolve(r))
        .catch((r) => q.reject(r));
    } else {
      q.resolve();
    }

    return q.promise as Promise<TODO>;
  }

  switchEvent(event: CartEvent, changeEvent: EventTimeSlot | EventDetail): Promise<any> {
    const found = this.getById(event.eventId);
    const q = this.$q.defer();

    if (found) {
      event.tickets.forEach((ticket) => {
        ticket.invalid = !this.checkEventHasTicket(ticket, changeEvent);
        ticket.eventId = changeEvent.id;
      });

      this.checkAndAddTickets(event, changeEvent);

      this.getAddOns(event).forEach((addOn) => {
        addOn.invalid = !this.checkEventHasAddOn(addOn, changeEvent);
        addOn.eventId = changeEvent.id;
      });

      this.events[found.index].eventId = changeEvent.id;

      return this.addToCart(event);
    } else {
      q.reject();
    }

    return q.promise as Promise<TODO>;
  }

  updateItem(item: CartEventTicket | CartEventAddOn): Promise<any> {
    const q = this.$q.defer();
    const found = this.getById(item.eventId);
    const existingItem = item.itemId && this.parent.getEventItemById(item as TODO as CartEventItem);
    const eventItem: CartEventItem = {
      quantity: item.quantity || 0,
      isRetail: item.isRetail || false,
      itemType: 'inventoryId' in item ? 'Inventory' : 'Event',
      eventId: item.eventId,
      displayOrder: item.displayOrder || 0,
      ticketingTypeId: 'ticketingTypeId' in item ? item.ticketingTypeId : null,
      inventoryId: 'inventoryId' in item ? String(item.inventoryId) : null,
      ticketingTypeName: 'ticketingTypeName' in item ? item.ticketingTypeName : null,
      inventoryName: 'inventoryName' in item ? item.inventoryName : null,
      unitPrice: item.unitPrice || null,
      retailUnitPrice:
        'inventoryId' in item ? item.unitPrice || null : (item as CartEventTicket).retailUnitPrice || null,
      ignoreEntitlements: item.isRetail ? false : item.ignoreEntitlements,
    };

    if (found) {
      eventItem.ignoreEntitlements = this.events[found.index].ignoreLimits ?? false;
    }

    if (existingItem) {
      eventItem.itemId = existingItem.itemId;
      if (item.invalid) {
        this.parent.removeItem(eventItem.itemId, true);
      } else {
        this.parent.updateItem(eventItem);
      }
      this.parent
        .updateCartItem(eventItem)
        .then((response) => {
          if (eventItem.itemType === 'Event') {
            this.updateReservation();
          }
          q.resolve(response);
        })
        .catch((r) => q.reject(r));
    }

    return q.promise as Promise<TODO>;
  }

  addItem(item: CartEventTicket | CartEventAddOn): Promise<TODO> {
    const q = this.$q.defer();
    const found = this.getById(item.eventId);
    const eventItem: CartEventItem = {
      quantity: item.quantity || 0,
      isRetail: item.isRetail || false,
      itemType: 'inventoryId' in item ? 'Inventory' : 'Event',
      eventId: item.eventId,
      displayOrder: item.displayOrder || 0,
      ticketingTypeId: 'ticketingTypeId' in item ? item.ticketingTypeId : null,
      inventoryId: 'inventoryId' in item ? String(item.inventoryId) : null,
      ticketingTypeName: 'ticketingTypeName' in item ? item.ticketingTypeName : null,
      inventoryName: 'inventoryName' in item ? item.inventoryName : null,
      unitPrice: item.unitPrice || null,
      retailUnitPrice:
        'inventoryId' in item ? item.unitPrice || null : (item as CartEventTicket).retailUnitPrice || null,
      ignoreEntitlements: item.isRetail ? false : item.ignoreEntitlements,
    };

    if (found) {
      eventItem.ignoreEntitlements = this.events[found.index].ignoreLimits ?? false;
    }

    this.parent
      .addCartItem(eventItem)
      .then((response) => {
        eventItem.itemId = response.data.payload;
        this.parent.addItem(eventItem);
        if (eventItem.itemType === 'Event') {
          this.updateReservation();
        }
        q.resolve(response);
      })
      .catch((r) => q.reject(r));

    return q.promise as Promise<TODO>;
  }

  addOrUpdateItems(
    event: CartEvent,
    items: (CartEventTicket | CartEventAddOn)[],
    itemType: 'Event' | 'Inventory',
  ): void {
    if (items.length && items[0] && items[0].displayOrder >= 0) {
      items.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    items.forEach((item: CartEventTicket | CartEventAddOn) => {
      const existingItem = item.itemId && this.parent.getEventItemById(item as TODO as CartEventItem);
      const eventItem: Partial<CartEventItem> = {
        quantity: item.quantity || 0,
        isRetail: item.isRetail || false,
        itemType: itemType,
        eventId: event.eventId,
        displayOrder: item.displayOrder || 0,
        ticketingTypeId: 'ticketingTypeId' in item ? item.ticketingTypeId : null,
        inventoryId: 'inventoryId' in item ? String(item.inventoryId) : null,
        ticketingTypeName: 'ticketingTypeName' in item ? item.ticketingTypeName : null,
        inventoryName: 'inventoryName' in item ? item.inventoryName : null,
        unitPrice: 'inventoryId' in item ? item.unitPrice || null : item.unitPrice || null,
        retailUnitPrice:
          'inventoryId' in item ? item.unitPrice || null : (item as CartEventTicket).retailUnitPrice || null,
        ignoreEntitlements: item.isRetail ? false : event.ignoreLimits,
      };
      if (existingItem) {
        eventItem.itemId = existingItem.itemId;
        if (item.invalid) {
          this.parent.removeItem(eventItem.itemId, true);
        } else {
          this.parent.updateItem(eventItem as CartEventItem);
        }
      } else {
        this.parent.addItem(eventItem as CartEventItem);
      }
    });
  }

  eventDetail(eventId: string): Promise<any> {
    const membershipLevelIds = this.parent.getTransactingMembershipCategory();
    const membershipId = this.parent.getTransactingMembershipId();
    return this.apiService.event.detail(eventId, { membershipCategoryId: membershipLevelIds, membershipId });
  }

  eventMembershipDetail(eventTemplateId: string): Promise<any> {
    return this.apiService.program.template.published({ eventTemplateId });
  }

  setModel(data: TODO[]): void {
    const eventsInCart: Record<string, CartEvent> = {};
    let found: ReturnType<typeof this.getById>;
    let eventDetailId: string | undefined;
    let eventId: string;

    this.model = data as typeof this.model;

    if (data instanceof Array && data.length) {
      data.forEach((item: TODO) => {
        if (item.eventId) {
          if (item.itemType === 'Event') {
            const result = this.addTicket(item, eventsInCart[item.eventId]);
            if (result) {
              eventsInCart[item.eventId] = result;
            }
          }
          if (item.itemType === 'Inventory') {
            const result = this.addAddon(item, eventsInCart[item.eventId]);
            if (result) {
              eventsInCart[item.eventId] = result;
            }
          }
        }
      });

      this.events = this.getEvents().filter((event) => {
        return eventsInCart.hasOwnProperty(event.eventId) && eventsInCart[event.eventId];
      });

      for (eventId in eventsInCart) {
        if (eventsInCart.hasOwnProperty(eventId) && eventsInCart[eventId]) {
          eventsInCart[eventId].ignoreLimits = this.salesRulesService.getIgnoreTicketLimits(eventsInCart[eventId]);

          found = this.getById(eventId);
          if (found) {
            this.events[found.index] = Object.assign(found.event, eventsInCart[eventId]);
            eventDetailId = this.events[found.index].event?.id;

            if (eventDetailId && eventDetailId === eventId) {
              this.updateTickets(this.events[found.index]);
              this.updateAddOns(this.events[found.index]);
            }
          } else {
            this.events.push(eventsInCart[eventId]);
          }
        }
      }
    } else {
      this.clearModel();
    }
  }

  getReservation(): Promise<any> {
    const q = this.$q.defer<TODO>();

    if (this.parent.getReservationId()) {
      this.apiService.reservations
        .detail(String(this.parent.getReservationId()))
        .then((r) => q.resolve(r))
        .catch((r) => q.reject(r));
    } else {
      this.createReservation()
        .then((r) => q.resolve(r))
        .catch((r) => q.reject(r));
    }

    return q.promise as Promise<TODO>;
  }

  setReservation(reservation?: any): void {
    this.reservation = reservation;
    if (this.reservation) {
      this.parent.setReservation(this.reservation.id);
    } else {
      this.parent.setReservation();
    }
  }

  updateReservation(): void {
    let latestReservation: { id: TODO; reservationItems: TODO[] };
    const createNewReservation = () => {
      this.createReservation(latestReservation.reservationItems)
        .then((r) => this.onReservationSuccess(r))
        .catch((r) => this.onReservationError(r));
    };

    if (!this.reservation) {
      this.reservation = {};
    }

    const latestReservationItems = this.getReservationItems();
    const hasChanged = JSON.stringify(latestReservationItems) !== JSON.stringify(this.reservation.reservationItems);

    if (hasChanged) {
      latestReservation = { id: this.reservation.id, reservationItems: latestReservationItems };

      if (latestReservation.id) {
        this.apiService.reservations
          .update(latestReservation)
          .then((updatedReservation) =>
            this.onReservationSuccess(
              updatedReservation as { payload: { reservationItems: TODO[]; expirationTimestamp: TODO } },
            ),
          )
          .catch((r) => {
            const responseErrorData = r.errorData;
            if (responseErrorData?.errorCode === this.CODES.errors.RESERVATION_DNE) {
              createNewReservation();
            } else {
              this.onReservationError(r);
            }
          });
      } else {
        createNewReservation();
      }
    }
  }

  getReservationItems(): { eventId: string; personType: string; quantity: number }[] {
    let reservationItems: { eventId: string; personType: string; quantity: number }[] = [];
    this.getEvents().forEach((event: CartEvent) => {
      reservationItems = reservationItems.concat(this.getTicketMap(event.eventId));
    });
    return reservationItems;
  }

  onReservationSuccess(updatedReservation: { payload: { reservationItems: TODO[]; expirationTimestamp: TODO } }): void {
    const updatedReservationItems = updatedReservation.payload.reservationItems.map((event: TODO) => ({
      quantity: event.quantity,
      eventId: event.eventId,
    }));

    if (this.reservation) {
      Object.assign(this.reservation, {
        expirationTimestamp: updatedReservation.payload.expirationTimestamp,
        reservationItems: updatedReservationItems,
      });
    }

    this.resetEventReservationErrors();
  }

  setReservationError(eventId: string, errorData: TODO): void {
    const event = this.getEvents().filter((e) => e.eventId === eventId);
    if (event[0]) {
      event[0].reservationError = errorData;
    }
  }

  onReservationError(response: { errorData: { eventId: TODO; requested: TODO; available: TODO } }): void {
    if (response.errorData && response.errorData.eventId) {
      const errorData = this.getReservationError(response.errorData);
      this.resetEventReservationErrors();
      this.setReservationError(response.errorData.eventId, errorData);
    }
  }

  getReservationError(errorData: { eventId: TODO; requested: TODO; available: TODO }): {
    requested: number;
    available: number;
  } {
    const currentReservationForErroringEvent =
      this.reservation?.reservationItems?.filter((e) => e.eventId === errorData.eventId) ?? [];
    const numberTicketsRequested = parseInt(errorData.requested, 10);
    let numberTicketsReserved = 0;

    if (currentReservationForErroringEvent[0]) {
      numberTicketsReserved = parseInt(currentReservationForErroringEvent[0].quantity, 10);
    }

    return {
      requested: numberTicketsRequested + numberTicketsReserved,
      available: errorData.available + numberTicketsReserved,
    };
  }

  resetEventReservationErrors(): void {
    this.getEvents().forEach((event) => {
      event.reservationError = false;
    });
  }

  hasReservation(): Partial<{ id: any; reservationItems: any[] }> | null {
    return this.reservation;
  }

  createReservation(reservationItems: TODO[] = []): Promise<any> {
    const q = this.$q.defer<{ payload: { reservationItems: TODO[]; expirationTimestamp: TODO } }>();

    this.apiService.reservations
      .create(reservationItems)
      .then((reservationResponse: TODO) => {
        this.setReservation(reservationResponse.payload);
        this.parent
          .updateCart()
          .then(() => q.resolve(reservationResponse))
          .catch(() => q.reject(reservationResponse));
      })
      .catch((r) => q.reject(r));

    return q.promise as Promise<TODO>;
  }

  cancelReservation(): void {
    if (this.reservation && this.reservation.id) {
      this.apiService.reservations.cancel(this.reservation.id);
      this.reservation = null;
      this.parent.setReservation();
    }
  }

  getModel(): any[] {
    return this.model;
  }

  resetModel(): void {
    this.model = [];
    this.events = [];
    this.salesRulesService.resetLimits();
    this.cancelReservation();
  }

  clearModel(): void {
    this.model = [];
    this.events = [];
    this.salesRulesService.resetLimits();
  }

  getEvents(): CartEvent[] {
    return this.events;
  }

  getById(id: string): { index: number; event: CartEvent } | false {
    let result: { index: number; event: CartEvent } | false = false;
    for (let i = 0, ii = this.events.length; i < ii; i++) {
      if (this.events[i].eventId === id) {
        result = { event: this.events[i], index: i };
        break;
      }
    }
    return result;
  }

  getSameDayEventTicketQuantities(): Record<string, { quantity: number }> | null {
    const sameDayEventTicketQuantities: Record<string, { quantity: number }> = {};
    const eventMap: Record<string, { events: CartEvent[] }> = {};
    const updateTicketQuantities = (event: CartEvent) => {
      event.tickets.forEach((ticket: CartEventTicket) => {
        let ticketKey = ticket.ticketingTypeId;
        if (ticket.quantity) {
          ticketKey = ticket.isRetail ? ticketKey + '-retail' : ticketKey + '-regular';
          if (sameDayEventTicketQuantities[ticketKey]) {
            sameDayEventTicketQuantities[ticketKey].quantity += ticket.quantity;
          } else {
            sameDayEventTicketQuantities[ticketKey] = { quantity: ticket.quantity };
          }
        }
      });
    };

    this.getEvents().forEach((event: CartEvent) => {
      if (!event.event) return;
      const startDate = event.event.startDate;
      const eventKey = event.event.templateId + '-' + startDate;
      if (!eventMap[eventKey]) {
        eventMap[eventKey] = { events: [event] };
      } else {
        eventMap[eventKey].events.push(event);
      }
    });

    for (const key in eventMap) {
      if (eventMap[key] && eventMap[key].events.length > 1) {
        eventMap[key].events.forEach((e) => updateTicketQuantities(e));
      }
    }

    return this.helpersService.isEmptyObject(sameDayEventTicketQuantities) ? null : sameDayEventTicketQuantities;
  }

  getEventTicketCount(id: string): number {
    const found = this.getById(id);
    const event = found ? found.event : null;
    if (!event) return 0;
    return event.tickets.reduce((count: number, item: CartEventTicket) => {
      let quantity = parseInt(String(item.quantity), 10);
      if (isNaN(quantity)) quantity = 0;
      return quantity + count;
    }, 0);
  }

  getTicketMap(id: string): { eventId: string; personType: string; quantity: number }[] {
    const found = this.getById(id);
    const event = found ? found.event : null;
    const result: { eventId: string; personType: string; quantity: number }[] = [];
    if (event) {
      for (const index in event.tickets) {
        const quantity = parseInt(String(event.tickets[index].quantity), 10);
        if (!isNaN(quantity)) {
          result.push({ eventId: id, personType: event.tickets[index].ticketingTypeId, quantity });
        }
      }
    }
    return result;
  }

  getDiscountedEventTicketCount(id: string): number {
    const found = this.getById(id);
    const event = found ? found.event : null;
    if (!event) return 0;
    return event.tickets.reduce((count: number, item: CartEventTicket) => {
      let quantity = 0;
      if (!item.isRetail && this.isTicketDiscounted(item, event)) {
        quantity = parseInt(String(item.quantity), 10);
        if (isNaN(quantity)) quantity = 0;
      }
      return quantity + count;
    }, 0);
  }

  getFullPriceEventTicketCount(id: string): number {
    const found = this.getById(id);
    const event = found ? found.event : null;
    if (!event) return 0;
    return event.tickets.reduce((count: number, item: CartEventTicket) => {
      let quantity = 0;
      if (item.isRetail || !this.isTicketDiscounted(item, event)) {
        quantity = parseInt(String(item.quantity), 10);
        if (isNaN(quantity)) quantity = 0;
      }
      return quantity + count;
    }, 0);
  }

  getAddOns(event: CartEvent | (Partial<EventDetail> & { ignoreLimits?: boolean })): CartEventAddOn[] {
    if ('addOns' in event && event.addOns) {
      if (Array.isArray(event.addOns)) return event.addOns;
      if ('inventoryAddOns' in event.addOns) return event.addOns.inventoryAddOns as TODO as CartEventAddOn[];
    }
    return [];
  }

  getEventAddOnCount(id: string): number {
    const found = this.getById(id);
    const event = found ? found.event : null;
    if (!event) return 0;
    return this.getAddOns(event).reduce((count: number, item: CartEventAddOn) => {
      let quantity = parseInt(String(item.quantity), 10);
      if (isNaN(quantity)) quantity = 0;
      return quantity + count;
    }, 0);
  }

  checkEventHasTicket(
    ticket: CartEventTicket,
    event: EventTimeSlot | EventDetail,
  ): { index: number; price: EventPrice } | null {
    let result: { index: number; price: EventPrice } | null = null;
    for (let i = 0, ii = event.priceList.prices.length; i < ii; i++) {
      if (event.priceList.prices[i].personType.id === ticket.ticketingTypeId) {
        result = { index: i, price: event.priceList.prices[i] };
        break;
      }
    }
    return result;
  }

  checkAndAddTickets(event: CartEvent, changeEvent: EventTimeSlot | EventDetail): void {
    const cartTicketObject = Object.assign({}, this.cartEventTicket);

    changeEvent.priceList.prices
      .map((ticket: EventPrice) =>
        Object.assign({}, cartTicketObject, {
          eventId: event.eventId,
          ticketingTypeId: ticket.personType.id,
          ticketingTypeName: ticket.personType.name,
          ticketingTypeDescription: ticket.personType.description,
          displayOrder: ticket.displayOrder,
          unitPrice: ticket.price,
          retailUnitPrice: ticket.price,
        }),
      )
      .forEach((ticket: typeof cartTicketObject) => {
        let ticketFound: CartEventTicket | undefined;
        if (event.tickets && event.tickets.length) {
          ticketFound = event.tickets.find((e: CartEventTicket) => ticket.ticketingTypeId === e.ticketingTypeId);
        }
        if (!ticketFound) {
          event.tickets.push(ticket as TODO as CartEventTicket);
        }
      });
  }

  checkEventHasAddOn(
    addOn: CartEventAddOn,
    event: EventTimeSlot | EventDetail,
  ): { index: number; addOn: EventInventoryAddOn } | null {
    let result: { index: number; addOn: EventInventoryAddOn } | null = null;
    const addOns = event.addOns ? event.addOns.inventoryAddOns : [];
    for (let i = 0, ii = addOns.length; i < ii; i++) {
      if (addOns[i].id === addOn.inventoryId) {
        result = { index: i, addOn: addOns[i] };
        break;
      }
    }
    return result;
  }

  addTicket(ticket: TODO, event: CartEvent | undefined): CartEvent | null {
    let result: CartEvent | null = null;
    let found: boolean | undefined;

    if (event) {
      if (!(event.tickets instanceof Array)) {
        event.tickets = [];
      }
      for (let i = 0, ii = event.tickets.length; i < ii; i++) {
        if (
          event.tickets[i].ticketingTypeId === ticket.ticketingTypeId &&
          event.tickets[i].isRetail === ticket.isRetail
        ) {
          found = true;
          event.tickets[i].quantity = ticket.quantity;
          event.tickets[i].resolvedQuantity = ticket.quantity;
          break;
        }
      }
      if (!found) {
        event.tickets.push(Object.assign(ticket, { resolvedQuantity: ticket.quantity }));
      }
      result = event;
    } else {
      result = {
        eventId: ticket.eventId,
        tickets: [Object.assign(ticket, { resolvedQuantity: ticket.quantity })],
        addOns: [],
        itemType: 'event',
      };
    }

    return result;
  }

  addAddon(addOn: TODO, event: CartEvent | undefined): CartEvent | null {
    let result: CartEvent | null = null;
    let found: boolean | undefined;

    if (event) {
      if (!(event.addOns instanceof Array)) {
        event.addOns = [];
      }
      for (let i = 0, ii = event.addOns.length; i < ii; i++) {
        if (event.addOns[i].inventoryId === addOn.inventoryId) {
          found = true;
          event.addOns[i].quantity = addOn.quantity;
          event.addOns[i].resolvedQuantity = addOn.quantity;
          break;
        }
      }
      if (!found) {
        event.addOns.push(Object.assign(addOn, { resolvedQuantity: addOn.quantity }));
      }
      result = event;
    } else {
      result = {
        eventId: addOn.eventId,
        addOns: [Object.assign(addOn, { resolvedQuantity: addOn.quantity })],
        tickets: [],
        itemType: 'event',
      };
    }

    return result;
  }

  setEventDetail(id: string, event: EventDetail): void {
    const found = this.getById(id);
    if (found) {
      this.events[found.index].event = this.processEvent(event);
      this.updateTickets(this.events[found.index]);
      this.updateAddOns(this.events[found.index]);
    }
  }

  updateTickets(event: CartEvent): void {
    event.tickets.forEach((ticket: CartEventTicket) => {
      const prices = event.event && event.event.priceList.prices ? event.event.priceList.prices : [];
      let found: boolean | undefined;
      for (let i = 0, ii = prices.length; i < ii; i++) {
        if (prices[i].personType.id === ticket.ticketingTypeId) {
          ticket.ticketingTypeDescription = prices[i].personType.description;
          ticket.csrDisabled = prices[i].csrDisabled;
          found = true;
          ticket.invalid = false;
          prices[i].unitPrice = ticket.unitPrice;
          prices[i].retailUnitPrice = ticket.retailUnitPrice;
          break;
        }
      }
      if (!found) {
        ticket.invalid = true;
        ticket.quantity = 0;
      }
    });
  }

  updateAddOns(event: CartEvent): void {
    (event.addOns || []).forEach((addOn: CartEventAddOn) => {
      let found: boolean | undefined;
      const addOns = event.event && event.event.addOns ? event.event.addOns.inventoryAddOns : [];
      for (let i = 0, ii = addOns.length; i < ii; i++) {
        if (addOns[i].id === addOn.inventoryId) {
          addOn.description = addOns[i].description;
          addOn.csrDisabled = addOns[i].csrDisabled;
          found = true;
          addOn.invalid = false;
          break;
        }
      }
      if (!found) {
        addOn.invalid = true;
        addOn.quantity = 0;
      }
    });
  }

  setEventMembershipDetail(id: string, data: TODO): void {
    const found = this.getById(id);
    const cartEvent = found ? this.events[found.index] : null;
    if (cartEvent?.event && data) {
      cartEvent.event.memberRestriction = this.processEventMemberRestriction(data);
    }
  }

  processEvent(event: EventDetail): EventDetail {
    event = this.normalizeEventDate(event);

    if (this.permissionsService.check('CSR')) {
      if (event.priceList) {
        event.priceList.prices.forEach((price: EventPrice) => {
          const filtered = (this.$filter('saleChannel') as any)([price], '!customerRep');
          price.csrDisabled = filtered.length;
        });
        const normal = event.priceList.prices.filter((v: EventPrice) => v.csrDisabled).sort(this.sortByDisplayOrder);
        const csr = event.priceList.prices.filter((v: EventPrice) => !v.csrDisabled).sort(this.sortByDisplayOrder);
        event.priceList.prices = ([] as EventPrice[]).concat(csr.slice(0), normal.slice(0));
      }

      if (event.addOns) {
        event.addOns.inventoryAddOns.forEach((addOn: EventInventoryAddOn) => {
          const filtered = (this.$filter('saleChannel') as any)([addOn], '!customerRep');
          if (filtered.length) {
            addOn.csrDisabled = true;
          }
        });
        event.addOns.inventoryAddOns = event.addOns.inventoryAddOns.sort(this.sortByCSR);
      }
    }

    return event;
  }

  normalizeEventDate(event: EventDetail): EventDetail {
    event.startDate = (this.$filter('date') as any)(
      this.calendarService.parseDate(event.startTime),
      this.userPreferencesService.getDateFormat('standardDate', 'dateFormat'),
    );
    event.startHour = (this.$filter('date') as any)(
      this.calendarService.parseDate(event.startTime),
      this.userPreferencesService.getDateFormat('standardTime', 'hourFormat'),
    );
    return event;
  }

  processEventMemberRestriction(data: TODO): EventMemberRestriction | null {
    const memberOnlyEvent = data && data.memberOnlyEvent;
    const allMembersCanPurchase = data && data.allMembersCanPurchase;
    let membershipLevels: TODO[] = [];

    if (memberOnlyEvent && !allMembersCanPurchase) {
      if (data.membership && data.membership.restrictions instanceof Array) {
        membershipLevels = data.membership.restrictions.map((restriction: TODO) => ({
          auxiliary: restriction.auxiliary,
          id: restriction.id,
          membershipLevelGroup: restriction.membershipLevelGroup,
          name: restriction.name,
        }));
      }
    }

    return memberOnlyEvent ? { memberOnlyEvent, restricted: !allMembersCanPurchase, membershipLevels } : null;
  }

  validateMemberRestriction(eventId: string): boolean {
    const membershipLevelIds = this.parent.getTransactingMembershipCategory();
    const membership = this.parent.getTransactingMembership();
    const membershipId = this.parent.getTransactingMembershipId();
    let memberRestriction: EventMemberRestriction | null | undefined;
    let isValid = true;
    const found = this.getById(eventId);
    let matchedLevels: TODO[] = [];
    const matchMembershipId = (membershipLevel: TODO) =>
      membershipLevelIds === membershipLevel.id || (membership && membership.categoryId === membershipLevel.id);

    if (found) {
      memberRestriction = found.event.event?.memberRestriction;
      if (memberRestriction && memberRestriction.memberOnlyEvent && memberRestriction.restricted) {
        if (membershipLevelIds || (membershipId && membership && membership.categoryId)) {
          matchedLevels = memberRestriction.membershipLevels.filter(matchMembershipId);
          isValid = matchedLevels.length > 0;
        } else {
          isValid = false;
        }
      }
    }

    return isValid;
  }

  sortByCSR(a: { csrDisabled?: boolean }, b: { csrDisabled?: boolean }): number {
    if (a.csrDisabled && !b.csrDisabled) return 1;
    return -1;
  }

  sortByDisplayOrder(a: { displayOrder?: number }, b: { displayOrder?: number }): number {
    return a.displayOrder && b.displayOrder ? a.displayOrder - b.displayOrder : 0;
  }

  remove(eventId: string): Promise<any> {
    const found = this.getById(eventId);
    const q = this.$q.defer();
    const itemIds: string[] = [];

    if (found) {
      if (found.event.tickets.length) {
        found.event.tickets.forEach((ticket: CartEventTicket) => {
          if (ticket.itemId) itemIds.push(ticket.itemId);
        });
      }
      if (found.event.addOns.length) {
        found.event.addOns.forEach((addOn: CartEventAddOn) => {
          if (addOn.itemId) itemIds.push(addOn.itemId);
        });
      }

      this.parent.removeItems(itemIds);
      this.events.splice(found.index, 1);

      this.parent
        .updateCart()
        .then((response: TODO) => {
          this.updateReservation();
          q.resolve(response);
        })
        .catch((r: TODO) => q.reject(r));
    } else {
      q.reject({ data: { error: { error: 'Event not found' } } });
    }

    return q.promise as Promise<TODO>;
  }

  salesRulesSuccess(data: TODO): void {
    const limits: TODO[] = data.violations instanceof Array ? data.violations : [];
    if (limits instanceof Array) {
      this.salesRulesService.processSalesRulesLimits(limits);
    }
    this.getEvents().forEach((event: CartEvent) => {
      event.limitsChecked = true;
      this.salesRulesService.updateTicketLimits(event, this.getSameDayEventTicketQuantities() as Record<string, any>);
    });
  }

  salesRulesError(_response: TODO): void {
    this.salesRulesService.processSalesRulesLimits([]);
    this.getEvents().forEach((event: CartEvent) => {
      this.salesRulesService.updateTicketLimits(event, this.getSameDayEventTicketQuantities() as Record<string, any>);
    });
  }

  validateSalesRules(_event: CartEvent): Promise<any> {
    const q = this.$q.defer();
    const checkLimits = this.hasSalesRules(this.getEvents());
    const cart = this.parent.getModel();

    if (cart && cart.id && checkLimits) {
      this.apiService.entitlements
        .validateSalesRules({ shoppingCartId: cart.id })
        .then((response: TODO) => {
          this.salesRulesSuccess(response.data.payload);
          q.resolve();
        })
        .catch((response: TODO) => {
          this.salesRulesError(response);
          q.reject();
        });
    } else {
      q.resolve();
    }

    return q.promise as Promise<TODO>;
  }

  hasSalesRules(_events: CartEvent[]): boolean {
    return true;
  }

  updateTicketLimitExceptions(event: TODO): Promise<any> {
    const found = this.getById(event.eventId);
    const q = this.$q.defer();

    if (found) {
      event.tickets.forEach((ticket: any) => {
        if (!ticket.isRetail) {
          ticket.ignoreEntitlements = event.ignoreLimits;
        } else {
          ticket.ignoreEntitlements = false;
        }
      });
      return this.addToCart(event);
    } else {
      q.reject();
    }

    return q.promise as Promise<TODO>;
  }

  getOrganizationOrder(): { events: any[]; organizationId: string } {
    const organization = this.parent.getOrganization();
    const organizationId = organization.id;
    const cartEvents = this.getEvents();
    let events: any[] = [];
    const promises = cartEvents
      .filter((cartEvent) => cartEvent.event)
      .map((cartEvent) => {
        const eventId = cartEvent.event?.id ?? '';
        return this.apiService.partners.reseller.event({ accountId: organizationId, eventId });
      });

    this.$q
      .all(promises)
      .then((responses: any) => {
        responses.forEach((response: any) => {
          events.push(response.data.payload);
        });
      })
      .catch(() => {
        cartEvents.forEach((cartEvent) => {
          events.push(cartEvent.event);
        });
      });

    events = events.sort((a, b) => {
      const aDate = this.calendarService.parseDate(a.eventStartDate || a.startTime).getTime();
      const bDate = this.calendarService.parseDate(b.eventStartDate || a.startTime).getTime();
      let result = 0;
      if (!isNaN(aDate) && !isNaN(bDate)) {
        if (aDate > bDate) result = 1;
        else if (aDate < bDate) result = -1;
      }
      return result;
    });

    return { events, organizationId };
  }

  getEventForms(): CartEventOrderForm[] {
    return this.events
      .filter((eventItem) => eventItem.event?.orderForms)
      .flatMap((eventItem) =>
        (eventItem.event?.orderForms ?? []).map((form: any) => ({
          ...form,
          uuid: `${form.id}-${eventItem.event?.id}`,
          event: {
            id: eventItem.event?.id ?? '',
            name: eventItem.event?.name ?? '',
            displayStartDate:
              (this.$filter('date') as any)(
                this.calendarService.parseDate(eventItem.event?.startTime),
                'EEEE, MMM d, yyyy',
              ) ?? '',
            displayStartTime:
              (this.$filter('date') as any)(this.calendarService.parseDate(eventItem.event?.startTime), 'h:mm a') ?? '',
            displayEndTime:
              (this.$filter('date') as any)(this.calendarService.parseDate(eventItem.event?.endTime), 'h:mm a') ?? '',
            startTime: eventItem.event?.startTime ?? '',
          },
          itemType: 'event' as const,
        })),
      );
  }
}